export const ROOM_PROTOCOL_VERSION = 1 as const;

export type JsonPrimitive = string | number | boolean | null;
export type JsonValue = JsonPrimitive | JsonValue[] | { [key: string]: JsonValue };

export type RoomRole = "host" | "co_host" | "moderator" | "player" | "spectator" | "guest" | "ai";

export type RoomLifecycle =
  | "creating"
  | "waiting"
  | "active"
  | "paused"
  | "reconnecting"
  | "restoring"
  | "ending"
  | "closed"
  | "archived"
  | "expired";

export type RoomLayout =
  | "bubbles"
  | "floating"
  | "rail"
  | "avatar_strip"
  | "grid"
  | "active_speaker"
  | "game_fullscreen"
  | "split"
  | "picture_in_picture"
  | "avatar_only"
  | "hidden_video"
  | "spectator"
  | "host_focused";

export type GameCategory = "board" | "party" | "trivia" | "story" | "social";
export type Capability = "text" | "audio" | "video" | "local_ai" | "cloud_ai";

export type RoomMessageType =
  | "room.join"
  | "room.leave"
  | "room.presence"
  | "room.member_updated"
  | "room.role_changed"
  | "room.settings_changed"
  | "room.layout_changed"
  | "room.locked"
  | "room.unlocked"
  | "room.closed"
  | "room.snapshot"
  | "chat.message"
  | "chat.updated"
  | "chat.deleted"
  | "chat.reaction"
  | "chat.typing"
  | "chat.receipt"
  | "media.state"
  | "media.active_speaker"
  | "media.connection_quality"
  | "media.layout"
  | "media.signal"
  | "media.signal_ack"
  | "game.selected"
  | "game.started"
  | "game.action"
  | "game.state"
  | "game.private_state"
  | "game.paused"
  | "game.resumed"
  | "game.ended"
  | "game.rematch"
  | "game.switched"
  | "ai.host_message"
  | "reaction.sent"
  | "moderation.action"
  | "tournament.match_state"
  | "reconnect.accepted"
  | "reconnect.rejected"
  | "error"
  | "heartbeat";

export interface RoomMessage<T = unknown> {
  version: typeof ROOM_PROTOCOL_VERSION;
  id: string;
  type: RoomMessageType;
  roomId: string;
  senderId?: string;
  clientTimestamp: number;
  serverTimestamp?: number;
  sequence?: number;
  payload: T;
}

export interface RoomParticipant {
  id: string;
  displayName: string;
  avatar: string;
  role: RoomRole;
  joinedAt: number;
  connected: boolean;
  /** Connected to Topzle and currently viewing this room in a visible tab. */
  activeInRoom?: boolean;
  microphone: boolean;
  camera: boolean;
  speaking: boolean;
  connectionQuality: "excellent" | "good" | "poor" | "offline";
  mediaSessionId?: string;
  audioTrackId?: string;
  videoTrackId?: string;
  identityType?: "registered" | "guest" | "ai";
  safetyMode?: "child" | "teen" | "adult";
  countryCode?: string;
}

export interface RoomSettings {
  title: string;
  selectedGameId?: string;
  visibility: "private" | "friends" | "group" | "public";
  joinPolicy: "open" | "invite" | "approval";
  microphoneDefault: boolean;
  cameraDefault: boolean;
  chatEnabled: boolean;
  reactionsEnabled: boolean;
  spectatorsAllowed: boolean;
  maximumParticipants: number;
  maximumSpectators: number;
  ageCategory: "all" | "teen" | "adult";
  language: string;
  aiPlayersEnabled: boolean;
  aiDifficulty: "easy" | "medium" | "hard";
  /** Game-specific bot policy, such as aggressive, defensive, or probability. */
  aiPolicy?: string;
  aiArrivalBehavior: "end_ai_game" | "spectate_ai_game";
}

export interface RoomGameResults {
  totalGames: number;
  draws: number;
  abandoned: number;
  plays: Record<string, number>;
  wins: Record<string, number>;
  playerNames: Record<string, string>;
}

export interface ActiveRoomSnapshot {
  roomId: string;
  code: string;
  lifecycle: RoomLifecycle;
  sequence: number;
  hostId: string;
  currentGameId: string | null;
  gameState: JsonValue | null;
  participants: RoomParticipant[];
  roomResults: RoomGameResults;
  settings: RoomSettings;
  layout: RoomLayout;
  updatedAt: number;
}

export interface ApiError {
  code: string;
  message: string;
  requestId: string;
  details?: unknown;
}

export type ApiResult<T> =
  | { ok: true; data: T; requestId: string }
  | { ok: false; error: ApiError };

export interface RoomSummary {
  id: string;
  code: string;
  title: string;
  hostId: string;
  lifecycle: RoomLifecycle;
  visibility: RoomSettings["visibility"];
  currentGameId: string | null;
  participantCount: number;
  createdAt: string;
}

export interface DashboardRoomSummary extends RoomSummary {
  role: RoomRole;
  joinedAt: string;
  updatedAt: string;
}

export interface UserDashboardSummary {
  rooms: DashboardRoomSummary[];
}

export type ReportCategory =
  | "harassment"
  | "hate"
  | "sexual_content"
  | "child_safety"
  | "self_harm"
  | "violence"
  | "threats"
  | "spam"
  | "cheating"
  | "impersonation"
  | "privacy_violation"
  | "illegal_content"
  | "unsafe_ai_content"
  | "other";

export interface GameResult {
  status: "completed" | "draw" | "abandoned";
  winnerIds: string[];
  /** Ordered finishers for race/ranking games. The first entry is the champion. */
  placements?: string[];
  scores: Record<string, number>;
  reason: string;
}

export interface GameContext {
  roomId: string;
  playerIds: string[];
  actorId?: string;
  now: number;
  randomInt?: (maximumExclusive: number) => number;
}

export interface ViewerContext {
  viewerId: string;
  role: RoomRole;
}

export interface BotContext {
  playerId: string;
  difficulty: "easy" | "medium" | "hard";
  policy?: string;
  now: number;
}

export interface ValidationResult {
  valid: boolean;
  code?: string;
  message?: string;
}

export interface AiHostContext {
  game: string;
  event: string;
  actor?: string;
  importance: "low" | "medium" | "high";
  facts: Record<string, string | number | boolean>;
}

export type AdminRole =
  | "super_admin"
  | "operations_admin"
  | "moderator"
  | "support_agent"
  | "content_manager"
  | "game_manager"
  | "ai_manager"
  | "analyst"
  | "read_only";

export type AnalyticsEventName =
  | "onboarding_started"
  | "onboarding_completed"
  | "guest_room_created"
  | "account_created"
  | "room_created"
  | "room_joined"
  | "room_join_failed"
  | "room_invite_sent"
  | "call_started"
  | "microphone_enabled"
  | "camera_enabled"
  | "layout_changed"
  | "game_selected"
  | "game_started"
  | "game_completed"
  | "game_abandoned"
  | "rematch_started"
  | "reconnect_started"
  | "reconnect_completed"
  | "reconnect_failed"
  | "chat_sent"
  | "reaction_sent"
  | "ai_host_enabled"
  | "ai_prompt_generated"
  | "local_model_downloaded"
  | "cloud_ai_used"
  | "report_submitted"
  | "friend_added"
  | "tournament_joined";
